// src/context/ChatContext.tsx
import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useMemo,
} from "react";

import { IContactDetailsTypes } from "../types/profile";
import { IContact, mapContactToUserTypes } from "../types/contacts";
import { useProfile } from "../hooks/useProfile";
import { useContacts } from "../hooks/useContacts";
import { useMessages } from "./MessagesContext";
import {
  IAttachmentTypes,
  IImageTypes,
  IMessage,
  IMessagesTypes,
  isWithin24Hours,
  mapAttachmentToTypes,
  mapImageToTypes,
  mapMessagesToMessagesTypes,
} from "../types/message";

import { playNotification, vibrate } from "../utils";
import avatar1 from "../assets/images/users/avatar-1.jpg";
// import { useAuth } from "./AuthContext";
import { useAuth } from "../context/AuthContext";

import { readConversation } from "../api";

interface ChatContextType {
  isAppReady: boolean;
  canSendText: boolean;
  canOpenChat: boolean;
  selectedChatId: number | null;
  conversationMessages: IMessagesTypes[];
  chatUserDetails: IContactDetailsTypes | null;

  // handleSelectChat: (id: number) => Promise<void>;
  handleSelectChat: (id: number) => void;
  clearSelectedChat: () => void;
  chatMenuContactList: IContact[];
}

type UpsertIncoming = {
  idContact: number;
  createdAt: string;
  isFromContact?: boolean;
  idUser?: number;
  idUserMain?: number;
};

const ChatContext = createContext<ChatContextType | null>(null);

export const ChatProvider = ({ children }: { children: ReactNode }) => {
  const didLoadRef = useRef(false);
  const { user } = useAuth();
  const { contactsMap, incrementUnread } = useContacts();


  const { chatUserDetails, setChatUserDetails } = useProfile();
  const [chatMenuContactList, setchatMenuContactList] = useState<IContact[]>([]);

  const [isAppReady, setIsAppReady] = useState(false);
  const [pendingChatId, setPendingChatId] = useState<number | null>(null);
  const [selectedChatId, setSelectedChatId] = useState<number | null>(null);
  const [conversationMessages, setConversationMessages] = useState<IMessagesTypes[]>([]);
  const [canSendText, setCanSendText] = useState(false);
  const [canOpenChat, setCanOpenChat] = useState(false);


  const {
    lastSignalRMessageRef,
    lastMessage,
    updatedMessage,
    markMessagesAsRead,
    contactBlocked,

    loadSessionContactOpen,
    loadMessagesTypes,
    loadAllUserConversations,
    loadMessagesNoUserAssigned,
    lastStatusUpdate,
    messages,


    // ⭐ IMPORTANTE PARA IMAGE/DOCUMENT
    getAttachmentsForMessage,
  } = useMessages();


  //Decide se ja fez toda leitura APP: Contatos e Conversas
  useEffect(() => {

    if (!user?.id) return;
    if (!contactsMap.size) return;
    if (pendingChatId) return;

    // aqui significa que:
    // - usuário carregou
    // - contactsMap populou
    // - nenhum chat pendente

    setIsAppReady(true);

  }, [user?.id, contactsMap, pendingChatId]);

  //Ajuda na referencia da mensagem
  const selectedChatIdRef = useRef<number | null>(null);
  useEffect(() => {
    selectedChatIdRef.current = selectedChatId;
  }, [selectedChatId]);


  // useEffect(() => {
  //   if (!updatedMessage) return;
  //   upsertChatLastMessage({
  //     idContact: updatedMessage.idContact,
  //     createdAt: updatedMessage.createdAt,
  //   });
  // }, [updatedMessage]);


  useEffect(() => {
    if (!selectedChatId || !chatUserDetails) return;

    const updatedContact = contactsMap.get(selectedChatId);
    if (!updatedContact) return;

    setCanSendText(canSendFreeTextByContact(updatedContact));

    const existBlocked = contactBlocked.some(c => c.id === updatedContact.id);

    // 🔁 Atualiza SOMENTE os dados dinâmicos
    setChatUserDetails(prev => {
      if (!prev) return prev;

      return {
        ...prev,
        basicDetails: {
          ...prev.basicDetails,
          unreadCount: updatedContact.unreadCount,
          lastMessage: updatedContact.lastMessageText,
          lastMessageTime: updatedContact.lastMessageTime,
          blockedMessage: updatedContact.blockedMessage,
          dataVisita: updatedContact.dataVisita,
          obsVisita: updatedContact.obsVisita,
          idRegiao: updatedContact.idRegiao,
          regiao: updatedContact.regiao,
          Blocked: existBlocked,
        },
      };
    });
  }, [contactsMap, selectedChatId]);

  // ---------------------------------------------------------------------
  // 🔵 AO INICIAR O SISTEMA → CARREGA TODAS AS CONVERSAS DO USUÁRIO
  //    E TAMBÉM AS MENSAGENS SEM USUÁRIO ATRIBUÍDO
  // ---------------------------------------------------------------------
  useEffect(() => {
    if (!user?.id || didLoadRef.current) return;

    didLoadRef.current = true;

    // console.log("🔥 PRELOAD RODANDO UMA VEZ");

    const preload = async () => {
      try {
        await loadAllUserConversations(user.id);
        await loadMessagesNoUserAssigned();
      } catch (e) {
        console.error("[ChatContext] erro ao preload", e);
      }
    };

    preload();
  }, [user?.id, loadAllUserConversations, loadMessagesNoUserAssigned]); // 👈 SOMENTE user.id  
  // useEffect(() => {

  //   if (!user?.id) return;

  //   // console.log("[ChatContext] useEffect", user.id);

  //   const preload = async () => {
  //     try {
  //       await loadAllUserConversations(user.id);

  //       await loadMessagesNoUserAssigned();

  //     } catch (e) {
  //       console.error("[ChatContext] erro ao sincronizar conversas", e);
  //     }
  //   };

  //   preload();
  // }, [user?.id, loadAllUserConversations, loadMessagesNoUserAssigned]);


  // ⭐ FUNÇÃO PARA CALCULAR ENVIO DE TEMPLATE OU NAO
  // ---------------------------------------------------------------------
  const canSendFreeTextByContact = (contact: IContact) => {
    // 🚫 Bloqueio por data
    if (contact.blockedMessage) {
      const blockedUntil = new Date(contact.blockedMessage);
      if (blockedUntil > new Date()) return false;
    }

    // ⏱️ Janela 24h
    return isWithin24Hours(contact.lastMessageTime);
  };

  // ---------------------------------------------------------------------
  // ⭐ FUNÇÃO PARA CONVERTER IMessage → IMessagesTypes (com attachments)
  // ---------------------------------------------------------------------
  const buildMessageTypes = useCallback(
    (msg: IMessage): IMessagesTypes => {

      const atts = getAttachmentsForMessage(msg.id) ?? [];
      const images: IImageTypes[] = [];
      const documents: IAttachmentTypes[] = [];

      atts.forEach(a => {
        if (a.mimeType.startsWith("image/")) {
          images.push(mapImageToTypes(a));

        } else {
          documents.push(mapAttachmentToTypes(a));
        }
      });

      return {
        ...mapMessagesToMessagesTypes([msg])[0],
        isRead: msg.isRead,
        reaction: msg.reaction,
        type: msg.type,
        time: msg.createdAt,

        idEnterprise: msg.idEnterprise,
        idUserMain: msg.idUserMain,
        idUser: msg.idUser,

        // campos corretos
        image: images.length > 0 ? images : undefined,
        attachments: documents.length > 0 ? documents : undefined,
      };
    },
    [getAttachmentsForMessage]
  );

  // ---------------------------------------------------------------------
  // ⭐ RECEBE MENSAGEM DO SIGNALR EM TEMPO REAL
  // ---------------------------------------------------------------------
  useEffect(() => {
    if (!updatedMessage) return;

    // 🔥 ATUALIZA MENU
    // upsertChatLastMessage({
    //   idContact: updatedMessage.idContact,
    //   createdAt: updatedMessage.createdAt
    // });
    // upsertChatLastMessage({
    //   idContact: updatedMessage.idContact,
    //   createdAt: updatedMessage.createdAt,
    //   isFromContact: updatedMessage.isFromContact,
    //   idUser: updatedMessage.idUser,
    //   idUserMain: updatedMessage.idUserMain
    // });
    upsertChatLastMessage({
      idContact: updatedMessage.idContact,
      createdAt: updatedMessage.createdAt,
      isFromContact: updatedMessage.isFromContact
    });

    const chatId = selectedChatIdRef.current;
    const cameFromSignalR =
      lastSignalRMessageRef.current?.id === updatedMessage.id;

    //mensagem não pertence ao chat aberto
    if (updatedMessage.idContact !== chatId) {
      if (cameFromSignalR && updatedMessage.isFromContact !== false) {
        // console.log('[upsertChatLastMessage][updatedMessage][count]', updatedMessage.idContact)

        playNotification();
        vibrate();
      }
      return;
    }

    // monta Types
    const mapped = buildMessageTypes(updatedMessage);

    setConversationMessages(prev => {
      // existe -> atualiza
      const exists = prev.some(m => m.mId === mapped.mId);

      if (exists) {
        return prev.map(m =>
          m.mId === mapped.mId ? { ...m, ...mapped } : m
        );
      }

      // não existe → adiciona
      return [...prev, mapped];
    });
  }, [updatedMessage]);

  // ---------------------------------------------------------------------
  // ⭐ STATUS (sent / delivered / read)
  // ---------------------------------------------------------------------
  useEffect(() => {
    if (!lastStatusUpdate) return;

    const { idMessage, status } = lastStatusUpdate;

    setConversationMessages(prev =>
      prev.map(msg => {
        if (msg.mId !== idMessage) return msg;

        const nextMeta = {
          ...msg.meta,
          reaction: msg.reaction,
          type: msg.type,
          sent: status === "sent",
          received: status === "delivered",
          read: status === "read",

        };

        return { ...msg, meta: nextMeta };
      })
    );
  }, [lastStatusUpdate]);


  //Esse Carrega o CHAT do Contato selecionado ( em quanto estava carregando...)
  useEffect(() => {

    if (!user?.id) return;
    if (!pendingChatId) return;

    const contact = contactsMap.get(pendingChatId);
    if (!contact) return;

    const loadPendingChat = async () => {

      console.log("[AutoSelect] contato agora carregado:", pendingChatId);

      const mappedContact = mapContactToUserTypes(contact);

      const existBlocked = contactBlocked.some(c => c.id === contact.id);

      // 1️⃣ monta primeiro (EVITA NULL FRAME)
      setChatUserDetails({
        basicDetails: {
          id: mappedContact.id,
          fullName: mappedContact.name,
          firstName: mappedContact.name,
          lastName: mappedContact.name,
          title: mappedContact.name,
          description: mappedContact.id + " - " + mappedContact.lastMessageText,
          email: mappedContact.email,
          location: "Brasil, BRA",
          avatar: avatar1,
          cover: "",
          idCliente: mappedContact.idCliente,
          idEnterprise: mappedContact.idEnterprise,
          name: mappedContact.fullName,
          cpfCnpj: mappedContact.cpfCnpj,
          phoneNumber: mappedContact.phoneNumber,
          address: mappedContact.address,
          situacao: mappedContact.situacao,
          obs: mappedContact.obs,
          obsAtendimento: mappedContact.obsAtendimento,
          unreadCount: mappedContact.unreadCount,
          lastMessage: mappedContact.lastMessageText,
          lastMessageTime: mappedContact.lastMessageTime,
          blockedMessage: mappedContact.blockedMessage,
          ativo: mappedContact.ativo,
          marked: mappedContact.marked,
          dataVisita: mappedContact.dataVisita,
          obsVisita: mappedContact.obsVisita,
          Blocked: existBlocked,
          in24hWindow: false
        },
        media: { total: 0, list: [] },
        attachedFiles: { total: 0, list: [] },
      });

      // 2️⃣ libera envio
      setCanSendText(canSendFreeTextByContact(contact));

      // 3️⃣ agora sim seleciona (render seguro)
      setSelectedChatId(pendingChatId);
      setPendingChatId(null);

      // 4️⃣ mensagens
      const msgs = await loadMessagesTypes(user.id, pendingChatId);
      setConversationMessages(msgs);

      // 5️⃣ leitura
      await readConversation(pendingChatId, user.id);
      markMessagesAsRead(pendingChatId);

    };

    loadPendingChat();

  }, [contactsMap, pendingChatId, user?.id]);


  // ---------------------------------------------------------------------
  // SELECT CHAT → CARREGA MENSAGENS COMPLETAS
  // ---------------------------------------------------------------------
  const handleSelectChat = useCallback(async (id: number): Promise<void> => {
    // console.log('[handleSelectChat] [id]', id);
    if (!user?.id) return;

    // console.log('[handleSelectChat] [useId]', id);

    const selectedContact = contactsMap.get(id);
    if (!selectedContact) {
      console.log("[handleSelectChat] contato ainda não carregado, enfileirando:", id);
      setPendingChatId(id);
      return;
    }

    //Aqui User IA
    const idUserIA = 1;

    // 1️⃣ seleciona primeiro
    setSelectedChatId(id);

    //Controla para nao deixa enviar MSG se ja existe uma Sessao ativa com outro atendente
    const openChat = await loadSessionContactOpen(selectedContact.id);
    setCanOpenChat(openChat);

    // 2️⃣ monta detalhes imediatamente (EVITA NULL FRAME 💥)
    const mappedContact = mapContactToUserTypes(selectedContact);

    // console.log('[handleSelectChat] [mappedContact]', mappedContact);

    const existBlocked = contactBlocked.some(c => c.id === selectedContact.id);

    setChatUserDetails(prev => {
      // 🛑 se for o mesmo contato E dados principais não mudaram → NÃO atualiza
      if (
        prev &&
        prev.basicDetails.id === mappedContact.id &&
        prev.basicDetails.lastMessage === mappedContact.lastMessageText &&
        prev.basicDetails.lastMessageTime === mappedContact.lastMessageTime &&
        prev.basicDetails.unreadCount === mappedContact.unreadCount
      ) {
        return prev; // 🔥 evita re-render total
      }

      // ✅ monta só quando realmente precisa
      return {
        basicDetails: {
          id: mappedContact.id,
          fullName: mappedContact.name,
          firstName: mappedContact.name,
          lastName: mappedContact.name,
          title: mappedContact.name,
          description: mappedContact.id + " - " + mappedContact.lastMessageText,
          email: mappedContact.email,
          location: "Brasil, BRA",
          avatar: avatar1,
          cover: "",

          //Dados Cliente
          idCliente: mappedContact.idCliente,
          idEnterprise: mappedContact.idEnterprise,
          name: mappedContact.fullName,
          cpfCnpj: mappedContact.cpfCnpj,
          phoneNumber: mappedContact.phoneNumber,
          address: mappedContact.address,
          situacao: mappedContact.situacao,
          obs: mappedContact.obs,
          obsAtendimento: mappedContact.obsAtendimento,
          foto1: mappedContact.foto1,
          foto2: mappedContact.foto2,
          foto3: mappedContact.foto3,
          arquivo1: mappedContact.arquivo1,
          arquivo2: mappedContact.arquivo2,

          unreadCount: mappedContact.unreadCount,
          lastMessage: mappedContact.lastMessageText,
          lastMessageTime: mappedContact.lastMessageTime,
          blockedMessage: mappedContact.blockedMessage,
          marked: mappedContact.marked,
          ativo: mappedContact.ativo,
          idRegiao: mappedContact.idRegiao,
          regiao: mappedContact.regiao,
          dataVisita: mappedContact.dataVisita,
          obsVisita: mappedContact.obsVisita,
          Blocked: existBlocked,
          in24hWindow: false
        },
        media: prev?.media ?? { total: 0, list: [] },
        attachedFiles: prev?.attachedFiles ?? { total: 0, list: [] },
      };
    });

    // setChatUserDetails({
    //   basicDetails: {
    //     id: mappedContact.id,
    //     fullName: mappedContact.name,
    //     firstName: mappedContact.name,
    //     lastName: mappedContact.name,
    //     title: mappedContact.name,
    //     description: mappedContact.id + " - " + mappedContact.lastMessageText,
    //     email: mappedContact.email, // "admin@themesbrand.com",
    //     location: "Brasil, BRA",
    //     avatar: avatar1,
    //     cover: "",

    //     //Dados Cliente (Finance)
    //     idCliente: mappedContact.idCliente,
    //     idEnterprise: mappedContact.idEnterprise,
    //     name: mappedContact.fullName,
    //     cpfCnpj: mappedContact.cpfCnpj,
    //     phoneNumber: mappedContact.phoneNumber,
    //     address: mappedContact.address,
    //     situacao: mappedContact.situacao,
    //     obs: mappedContact.obs,
    //     obsAtendimento: mappedContact.obsAtendimento,
    //     foto1: mappedContact.foto1,
    //     foto2: mappedContact.foto2,
    //     foto3: mappedContact.foto3,
    //     arquivo1: mappedContact.arquivo1,
    //     arquivo2: mappedContact.arquivo2,

    //     unreadCount: mappedContact.unreadCount,
    //     lastMessage: mappedContact.lastMessageText,
    //     lastMessageTime: mappedContact.lastMessageTime,
    //     blockedMessage: mappedContact.blockedMessage,
    //     marked: mappedContact.marked,
    //     ativo: mappedContact.ativo,
    //     idRegiao: mappedContact.idRegiao,
    //     regiao: mappedContact.regiao,
    //     dataVisita: mappedContact.dataVisita,
    //     obsVisita: mappedContact.obsVisita,
    //     Blocked: existBlocked,
    //     in24hWindow: false
    //   },
    //   media: { total: 0, list: [] },
    //   attachedFiles: { total: 0, list: [] },
    // });


    // 4️⃣ carrega mensagens
    const msgs = await loadMessagesTypes(user.id, id);
    setConversationMessages(msgs);

    // 3️⃣ calcula envio livre
    setCanSendText(canSendFreeTextByContact(selectedContact));
    // console.log('[handleSelectChat] [canSendFreeTextByContact]', canSendText);

    // 5️⃣ marca como lida
    await readConversation(id, user.id);
    markMessagesAsRead(id);

  }, [contactsMap, user]);

  // ---------------------------------------------------------------------
  // CLEAN CHAT
  // ---------------------------------------------------------------------
  const clearSelectedChat = useCallback(() => {
    // console.log("🚨 clearSelectedChat DISPARADO");
    setSelectedChatId(null);
    setConversationMessages([]);
    setChatUserDetails(null);
  }, []);

  //Menu lateral com base Chat
  // const upsertChatLastMessage = useCallback((incoming: {
  //   idContact: number;
  //   createdAt: string;
  // }) => {

  //   setchatMenuContactList(prev => {

  //     const contact = contactsMap.get(incoming.idContact);
  //     if (!contact) return prev;

  //     const index = prev.findIndex(c => c.id === incoming.idContact);

  //     // cria contato atualizado
  //     const updated: IContact = {
  //       ...contact,
  //       lastMessageTime: incoming.createdAt
  //     };

  //     // 👉 NÃO EXISTE → entra no topo
  //     if (index === -1) {
  //       return [updated, ...prev];
  //     }

  //     // 👉 EXISTE → SOBE
  //     const next = [...prev];
  //     next.splice(index, 1);

  //     return [updated, ...next];
  //   });

  // }, [contactsMap]);
  // const upsertChatLastMessage = useCallback((incoming: UpsertIncoming) => {
  //   console.log('upsertChatLastMessage', incoming)

  //   setchatMenuContactList(prev => {
  //     console.log('upsertChatLastMessage', incoming.idContact)

  //     const contact = contactsMap.get(incoming.idContact);
  //     if (!contact) return prev;

  //     const isMine = incoming.idUser === user?.id;
  //     const isUnassigned = incoming.idUser === incoming.idUserMain;

  //     // ignora outro atendente
  //     if (!isMine && !isUnassigned) return prev;

  //     const isOpened = selectedChatIdRef.current === incoming.idContact;

  //     let unread = contact.unreadCount ?? 0;

  //     // 📬 incrementa se:
  //     if (
  //       incoming.isFromContact &&
  //       !isOpened
  //     ) {
  //       unread += 1;
  //     }

  //     const updated: IContact = {
  //       ...contact,
  //       lastMessageTime: incoming.createdAt,
  //       unreadCount: unread
  //     };

  //     const index = prev.findIndex(c => c.id === incoming.idContact);

  //     if (index === -1) {
  //       return [updated, ...prev];
  //     }

  //     const next = [...prev];
  //     next.splice(index, 1);

  //     return [updated, ...next];

  //   });

  // }, [contactsMap, user?.id]);

  const upsertChatLastMessage = useCallback((incoming: {
    idContact: number;
    createdAt: string;
    isFromContact?: boolean;
  }) => {

    setchatMenuContactList(prev => {

      const contact = contactsMap.get(incoming.idContact);
      if (!contact) return prev;

      const index = prev.findIndex(c => c.id === incoming.idContact);

      const shouldIncrementUnread =
        incoming.isFromContact === true &&
        selectedChatIdRef.current !== incoming.idContact;

      const updated: IContact = {
        ...contact,
        lastMessageTime: incoming.createdAt,
        unreadCount: shouldIncrementUnread
          ? (contact.unreadCount ?? 0) + 1
          : contact.unreadCount ?? 0
      };

      if (index === -1) {
        return [updated, ...prev];
      }

      const next = [...prev];
      next.splice(index, 1);

      return [updated, ...next];
    });

  }, [contactsMap]);

  const contextValue = useMemo(() => ({
    isAppReady,
    canSendText,
    canOpenChat,
    selectedChatId,
    conversationMessages,
    chatUserDetails,
    handleSelectChat,
    clearSelectedChat,
    chatMenuContactList,
  }), [
    isAppReady,
    canSendText, //canSendFreeTextByContact,
    canOpenChat,
    selectedChatId,
    conversationMessages,
    chatUserDetails,
    handleSelectChat,
    clearSelectedChat,
    chatMenuContactList,
  ]);


  return (
    <ChatContext.Provider value={contextValue}>
      {children}
    </ChatContext.Provider>
  );
};

export const useChat = () => {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error("useChat must be used inside ChatProvider");
  return ctx;
};
